// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import {
  finalize
} from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { FireSafetyDocsService } from 'src/app/shared/services/fire-safety-docs.service';
import { HelperService } from "src/app/shared/services/helper.service";


/**
 * Script start
 */

@Component({
  selector: "edit-fire-safety-doc-dialog",
  templateUrl: "./edit-fire-safety-doc-dialog.component.html",
  styleUrls: ["./edit-fire-safety-doc-dialog.component.scss"]
})
export class FireSafetyDocsEditDocDialogComponent
  implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = true; // Used to view/hide form loading spinner

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Edit item form

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<FireSafetyDocsEditDocDialogComponent>,
    public cancelContractDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private fireSafetyDocsService: FireSafetyDocsService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    this.dialogIsLoading = false;

    this.initForm();

  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }


  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    this.fields = [];
    this.model = this.data;
    // Set fields
    this.fields = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12',
            type: 'input',
            key: 'name',
            templateOptions: {
              label: this.translateService.instant('companies.fields.fireSafetyDocs.name'),
              placeholder: this.translateService.instant('companies.fields.fireSafetyDocs.name'),
              required: true,
              minLength: 2,
              maxLength: 100,
            },
          },
          {
            className: 'col-12',
            type: 'textarea',
            key: 'description',
            templateOptions: {
              label: this.translateService.instant('companies.fields.fireSafetyDocs.description'),
              placeholder: this.translateService.instant('companies.fields.fireSafetyDocs.description'),
              required: true,
              minLength: 2,
              maxLength: 1000,
              rows: 5,
            },
          },
          {
            className: "col-12",
            type: "file-uploader",
            key: "files",
            templateOptions: {
              required: true,
              allowedFilesNumber: 200,
              allowedFilesExtensions: this.configService.allowedDocumentExtensions,
              customLabel: this.translateService.instant('companies.fields.fireSafetyDocs.file'),
            },
          }
        ],
      },
    ];
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(): void {
    this.dialogRef.close(false);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {

    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      // Set errors inside invalid form controls
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.controls[key];
        if (control.invalid) {
          control.markAsTouched();
          control.setErrors({
            required: true,
          });
        }
      });
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;


    if (this.form.value) {
      this.subscriptions.push(
        this.fireSafetyDocsService.update
          (
            this.data.companyId,
            this.data._id,
            this.form.value
          )
          .pipe(finalize(() => (this.formBtnOptions.active = false)))
          .subscribe(
            res => {
              if (res.status) {
                this.toastrService.success(res.message);
                this.dialogRef.close(true);
              }
            },
            err => {
              this.helperService.handleFormlyError(this.form, err);
            }
          )
      );

    }
  }
}